/**
 * Copyright (C) Siemens AG 1996-2025 ALL RIGHTS RESERVED.
 */

import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import {Location} from '@angular/common';
import {firstValueFrom, Subscription, take} from 'rxjs';
import {IxModule} from '@siemens/ix-angular';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {PassportService} from '../../../../../services/passport.service';
import {FormCategory} from '../../../../../models/FormCategory';
import {BatteryPassAttributeLabel} from '../../../../../models/BatteryPassAttributeLabel';
import {UserService} from '../../../../../services/user.service';
import {AssignmentService} from '../../../../../services/assignment.service';
import {NormalisedApiError} from '../../../../../models/errors/api-error';
import {datadogLogs} from '@datadog/browser-logs';

@Component({
  selector: 'app-category-workflow',
  imports: [IxModule, TranslateModule],
  templateUrl: './category-workflow.component.html',
  styleUrl: './category-workflow.component.scss',
})
export class CategoryWorkflowComponent implements OnInit, OnChanges, OnDestroy {
  readonly location = inject(Location);
  readonly passportService = inject(PassportService);
  readonly userService = inject(UserService);
  readonly assignmentService = inject(AssignmentService);
  private readonly translate = inject(TranslateService);

  @Input() passportId?: string;
  @Input() schemaId!: string;
  @Input() purpose!: string;
  @Output() categorySelected: EventEmitter<number> = new EventEmitter<number>();

  selectedCategory = 0;
  categories?: FormCategory[];
  categoriesWithErrors: string[] = [];

  /** Holds a validation error that arrived before the schema finished loading. */
  private pendingError: NormalisedApiError | null = null;

  loading = true;

  private changeLangSubscription?: Subscription;
  private unprocessableEntityErrorSubscription?: Subscription;
  private passportUpdateSuccessSubscription?: Subscription;

  /**
   * Monotonically increasing counter used to discard stale results from
   * overlapping `reprocessFormForLanguage()` calls when the user switches
   * languages in rapid succession (e.g. en → de → fr).
   */
  private reprocessGeneration = 0;

  ngOnInit() {
    this.unprocessableEntityErrorSubscription =
      this.passportService.onUnprocessableEntityError.subscribe((error) => {
        if (error) {
          this.markCategoryWithError(error);
        } else {
          this.categoriesWithErrors = [];
        }
      });
    this.passportUpdateSuccessSubscription =
      this.passportService.onPassportUpdateSuccess.subscribe((passport) => {
        if (passport) {
          this.categoriesWithErrors = [];
        }
      });

    // Re-process the form in the newly selected language once ngx-translate
    // has finished loading it. The language selection/loading itself is
    // already handled elsewhere; here we only react to the resulting event.
    this.changeLangSubscription = this.translate.onLangChange.subscribe(() => {
      this.reprocessFormForLanguage();
    });
  }

  ngOnDestroy(): void {
    this.changeLangSubscription?.unsubscribe();
    this.unprocessableEntityErrorSubscription?.unsubscribe();
    this.passportUpdateSuccessSubscription?.unsubscribe();
  }

  /**
   * Reloads and re-maps the schema for the currently selected schemaId so that
   * category names and attribute labels reflect the active language, without
   * losing the currently selected category/tab or triggering a page reload.
   */
  private reprocessFormForLanguage(): void {
    if (!this.schemaId) {
      return;
    }
    const generation = ++this.reprocessGeneration;
    this.loadSchema(this.schemaId).then((schema) => {
      // Discard this result if a newer language switch has started in the meantime.
      if (generation !== this.reprocessGeneration) {
        return;
      }
      this.categories = schema;

      if (this.pendingError) {
        this.markCategoryWithError(this.pendingError);
        this.pendingError = null;
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['schemaId'] && changes['schemaId'].currentValue) {
      this.loadSchema(changes['schemaId'].currentValue).then((schema) => {
        this.categories = schema;
        this.setCategory();
        this.loading = false;

        // Process any validation error that arrived before the schema was ready.
        if (this.pendingError) {
          this.markCategoryWithError(this.pendingError);
          this.pendingError = null;
        }
      });
    }
  }

  async loadSchema(schemaId: string): Promise<FormCategory[]> {
    if (this.purpose !== 'assignment') {
      return firstValueFrom(
        this.passportService.loadMappedSchema$(schemaId, this.passportId),
      );
    } else {
      const openAttributes = await this.loadOpenAttributes();
      const schema = await firstValueFrom(
        this.passportService.loadMappedSchema$(schemaId, this.passportId),
      );
      return this.assignmentService.filterAttributesByAssignments(
        schema,
        openAttributes,
      );
    }
  }

  markCategoryWithError(error: NormalisedApiError) {
    if (!this.categories?.length) {
      datadogLogs.logger.warn(
        'markCategoryWithError called before categories were loaded — buffering error',
        {schemaId: this.schemaId},
      );
      this.pendingError = error;
      return;
    }

    this.categoriesWithErrors = [];
    const categoryList = this.categories.map((c) => c.name);
    error.all.forEach((entry) => {
      const pointer = entry.source?.pointer ?? '';
      if (!pointer) {
        // No field pointer: scan detail text for matching category names
        categoryList.forEach((category) => {
          if (entry.detail.includes(category)) {
            this.categoriesWithErrors.push(category);
          }
        });
      } else {
        // Strip the RFC 6901 leading slash and take the first path segment (supports / and . as separators)
        const firstSegment = pointer.replace(/^\//, '').split(/[/.]/)[0];
        this.categoriesWithErrors.push(firstSegment);
      }
    });
  }

  setFormIndex(index: number) {
    if (
      this.categories &&
      (isNaN(index) || index > this.categories.length - 1 || index < 0)
    ) {
      index = 0;
    }
    this.selectedCategory = index;
    this.categorySelected.next(index);
    this.location.replaceState(`${this.location.path()}#${String(index)}`);
  }

  setCategory() {
    const fragment = this.location.path(true).split('#')[1];
    this.setFormIndex(Number(fragment) || 0);
  }

  async loadOpenAttributes(): Promise<BatteryPassAttributeLabel[]> {
    const user = await firstValueFrom(
      this.userService.userDataChange.pipe(take(1)),
    );
    const attributes = await this.assignmentService.loadAttributeAssignments(
      user?.id,
      this.passportId,
    );
    return attributes.filter((assignment) => assignment.status !== 'complete');
  }
}
